import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowRight } from 'lucide-react';
import { useMember } from '../access/MemberAuthContext';
import EnquiryMemberGate from './EnquiryMemberGate';
import { track } from '../lib/track';

const API = process.env.REACT_APP_BACKEND_URL;

const THEME_OPTIONS = {
  home: ['Coastal Exploration', 'Aviation & Travel', 'Trains & Transportation', 'Future City', 'Other / Not sure'],
  events: ['Cosmic Adventure', 'Underwater World', 'Aviation & Travel', 'Celebration (freeform)', 'Other / Not sure'],
};

const PARTICIPANT_OPTIONS = ['1–4', '5–8', '9–12', '13–20', '20+'];

/**
 * ServiceEnquiryForm — shared enquiry form for Experiences at Home and
 * Celebrations & Events. Posts to /api/experiences/enquiry with the
 * pre-selected serviceType ('home' or 'events').
 * Spec: this is an enquiry, NOT a confirmed booking.
 */
const ServiceEnquiryForm = ({ serviceType, headline, submitLabel }) => {
  const [form, setForm] = useState({
    theme: '',
    participants: '',
    location: '',
    preferredDate: '',
    name: '',
    email: '',
    phone: '',
    preferences: '',
    marketingOptIn: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const { member } = useMember();

  useEffect(() => {
    if (!member) return;
    const fullName = [member.first_name, member.last_name].filter(Boolean).join(' ');
    setForm((f) => ({ ...f, name: f.name || fullName, email: member.email }));
  }, [member]);

  const update = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Please share your name so we can respond.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/experiences/enquiry`, { serviceType, ...form }, { withCredentials: true });
      track('form_submit', 'experiences-enquiry');
      setDone(true);
      toast.success('Thank you. Your enquiry has been received — this is not a confirmed booking.');
    } catch {
      toast.error('Something went wrong. Please try again or email hello@n3uro.world.');
    } finally {
      setSubmitting(false);
    }
  };

  if (member === null) {
    return <div className="service-enquiry-form" data-testid="enquiry-member-loading"><p className="service-enquiry-note">Checking your N3URO ACCESS session…</p></div>;
  }
  if (member === false) return <EnquiryMemberGate kind={'send an enquiry'} />;

  if (done) {
    return (
      <div className="service-enquiry-form service-enquiry-done" data-testid="experience-enquiry-done">
        <h3>Thank you.</h3>
        <p>
          Your enquiry has been received. <strong>This is not a confirmed booking.</strong> A member of
          the N3URO WORLD® team will be in touch to discuss availability and next steps.
        </p>
      </div>
    );
  }

  return (
    <form className="service-enquiry-form" onSubmit={submit} data-track-form="experiences-enquiry" data-testid="experience-enquiry-form" noValidate>
      {headline && <h3 className="service-enquiry-headline">{headline}</h3>}

      <label className="service-enquiry-field">
        <span>Theme</span>
        <select value={form.theme} onChange={update('theme')} data-testid="field-theme">
          <option value="">Select a theme (optional)</option>
          {THEME_OPTIONS[serviceType]?.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </label>

      <div className="service-enquiry-row">
        <label className="service-enquiry-field">
          <span>Approximate participants</span>
          <select value={form.participants} onChange={update('participants')} data-testid="field-participants">
            <option value="">Select…</option>
            {PARTICIPANT_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>

        <label className="service-enquiry-field">
          <span>Preferred date (optional)</span>
          <input type="text" placeholder="e.g. a weekend in March" value={form.preferredDate} onChange={update('preferredDate')} data-testid="field-preferred-date" />
        </label>
      </div>

      <label className="service-enquiry-field">
        <span>Area or postcode</span>
        <input type="text" placeholder="e.g. SW1A, Greater London, remote" value={form.location} onChange={update('location')} data-testid="field-location" />
      </label>

      <div className="service-enquiry-row">
        <label className="service-enquiry-field">
          <span>Your name <span className="service-enquiry-req">*</span></span>
          <input type="text" required value={form.name} onChange={update('name')} data-testid="field-name" />
        </label>
        <label className="service-enquiry-field">
          <span>Email (from your account)</span>
          <input type="email" value={form.email} readOnly aria-readonly="true" title="Your N3URO ACCESS email" data-testid="field-email" />
        </label>
      </div>

      <label className="service-enquiry-field">
        <span>Phone (optional)</span>
        <input type="tel" value={form.phone} onChange={update('phone')} data-testid="field-phone" />
      </label>

      <label className="service-enquiry-field">
        <span>Preferences or notes (optional)</span>
        <textarea rows={4} placeholder="Any sensory, access or practical preferences the team should know about." value={form.preferences} onChange={update('preferences')} data-testid="field-preferences" />
      </label>

      <label className="service-enquiry-opt">
        <input type="checkbox" checked={form.marketingOptIn} onChange={update('marketingOptIn')} data-testid="field-marketing" />
        <span>I'm happy to hear about future N3URO WORLD® experiences by email.</span>
      </label>

      <p className="service-enquiry-note">
        This is an enquiry, not a confirmed booking. We'll be in touch to agree details.
      </p>

      <button type="submit" className="service-enquiry-submit" disabled={submitting} data-testid="experience-enquiry-submit">
        <span>{submitting ? 'Sending…' : (submitLabel || 'Send enquiry')}</span>
        {!submitting && <ArrowRight size={16} strokeWidth={2.4} />}
      </button>
    </form>
  );
};

export default ServiceEnquiryForm;
